/**
 * errorBuffer.js — captures the last 5 client-side errors so they can be
 * attached to feedback submissions automatically.
 *
 * Hooks:
 *   - window.onerror         (uncaught runtime errors)
 *   - unhandledrejection     (uncaught promise rejections)
 *   - console.error          (explicit error logging from anywhere in the app)
 *
 * Errors are kept in a small ring buffer. Truncated to keep payload small.
 */

const MAX_ERRORS = 5;
const MAX_MESSAGE_LEN = 500;
const MAX_STACK_LEN = 1500;

const buffer = [];
let installed = false;

const truncate = (s, n) => {
  if (s == null) return '';
  const str = typeof s === 'string' ? s : (() => {
    try { return JSON.stringify(s); } catch { return String(s); }
  })();
  return str.length > n ? str.slice(0, n) + '…' : str;
};

const push = (entry) => {
  buffer.push({
    ts: new Date().toISOString(),
    ...entry,
  });
  while (buffer.length > MAX_ERRORS) buffer.shift();
};

export const installErrorBuffer = () => {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // Uncaught runtime errors
  window.addEventListener('error', (event) => {
    push({
      source: 'window.onerror',
      message: truncate(event?.message || event?.error?.message || 'Unknown error', MAX_MESSAGE_LEN),
      stack: truncate(event?.error?.stack || '', MAX_STACK_LEN),
      file: truncate(event?.filename || '', 200),
      line: event?.lineno,
      col: event?.colno,
    });
  });

  // Uncaught promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    push({
      source: 'unhandledrejection',
      message: truncate(reason?.message || reason || 'Unhandled promise rejection', MAX_MESSAGE_LEN),
      stack: truncate(reason?.stack || '', MAX_STACK_LEN),
    });
  });

  // Wrap console.error to capture explicit logs
  const originalError = console.error.bind(console);
  console.error = (...args) => {
    try {
      const message = args
        .map((a) => (a instanceof Error ? a.message : typeof a === 'string' ? a : (() => {
          try { return JSON.stringify(a); } catch { return String(a); }
        })()))
        .join(' ');
      const stack = args.find((a) => a instanceof Error)?.stack || '';
      push({
        source: 'console.error',
        message: truncate(message, MAX_MESSAGE_LEN),
        stack: truncate(stack, MAX_STACK_LEN),
      });
    } catch {
      // Never let logging break logging
    }
    originalError(...args);
  };
};

export const getRecentErrors = () => buffer.slice();

export const clearErrorBuffer = () => {
  buffer.length = 0;
};

export default { installErrorBuffer, getRecentErrors, clearErrorBuffer };
